import * as React from "react";
import { AlertTriangle, CircleDashed, RefreshCw } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { useTenantPathFn } from "@/lib/tenant";
import { isNoFinishedScan } from "@/lib/noFinishedScan";

/**
 * The ONE error panel.
 *
 * sp-migrate copy-pastes the same red div into seven files, so seven pages
 * report failures seven slightly different ways and none of them shows the
 * request id. This is that div, once.
 *
 * It shows WHAT FAILED and the server's own message. It never substitutes a
 * reassuring generic sentence: "the tenant credential store is not configured
 * on this deployment" is the answer the operator needs, and "Something went
 * wrong" is not.
 */
export interface ErrorPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** What the app was doing, e.g. "load the posture findings". */
  action: string;
  /** The thrown value. ApiError carries the status, code and request id. */
  error: unknown;
  /** Wired to a retry when the caller has one; omitted when it does not. */
  onRetry?: () => void;
}

interface Described {
  message: string;
  status: number | null;
  code: string | null;
  requestId: string | null;
  fields: Array<{ field: string; message: string }>;
}

export function describeError(error: unknown): Described {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      status: error.status,
      code: error.code,
      requestId: error.requestId,
      fields: error.details,
    };
  }
  if (error instanceof Error) {
    return {
      message: error.message,
      status: null,
      code: null,
      requestId: null,
      fields: [],
    };
  }
  return {
    message: String(error),
    status: null,
    code: null,
    requestId: null,
    fields: [],
  };
}

export function ErrorPanel({
  action,
  error,
  onRetry,
  className,
  ...props
}: ErrorPanelProps): React.ReactElement {
  const described = describeError(error);
  if (isNoFinishedScan(error)) {
    return <NoFinishedScanPanel onRetry={onRetry} className={className} {...props} />;
  }
  return (
    <div
      role="alert"
      data-testid="error-panel"
      data-status={described.status ?? "none"}
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-state-fail/30 bg-state-fail-bg p-4",
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-state-fail" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-state-fail">Could not {action}</p>
          <p className="text-sm text-ink-2" data-testid="error-panel-message">
            {described.message}
          </p>
          {described.fields.length > 0 ? (
            <ul className="mt-1 list-disc pl-4 text-sm text-ink-2">
              {described.fields.map((f) => (
                <li key={`${f.field}:${f.message}`}>
                  <span className="font-mono text-xs">{f.field}</span> {f.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-7 text-xs text-ink-3">
        {described.status !== null ? (
          <span>
            HTTP <span className="font-mono">{described.status}</span>
          </span>
        ) : null}
        {described.code !== null ? (
          <span>
            Code <span className="font-mono">{described.code}</span>
          </span>
        ) : null}
        {described.requestId !== null ? (
          <span>
            Request <span className="font-mono">{described.requestId}</span>
          </span>
        ) : null}
      </div>

      {onRetry !== undefined ? (
        <div className="pl-7">
          <Button variant="outline" size="sm" onClick={onRetry} data-testid="error-panel-retry">
            <RefreshCw aria-hidden="true" />
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function NoFinishedScanPanel({
  onRetry,
  className,
  ...props
}: Omit<ErrorPanelProps, "action" | "error">): React.ReactElement {
  const to = useTenantPathFn();
  return (
    <div
      role="status"
      data-testid="no-finished-scan"
      className={cn(
        "hatch flex flex-col gap-3 rounded-lg border border-state-nc/30 p-4",
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-3">
        <CircleDashed className="mt-0.5 h-4 w-4 shrink-0 text-state-nc" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-ink">Not measured yet</p>
          <p className="text-sm text-ink-2">
            No scan has finished for this tenant, so there is nothing to report. This is not a clean
            result: nothing has been checked.
          </p>
          <p className="text-sm text-ink-2">
            Next: connect the tenant under{" "}
            <a className="underline" href={to("/admin/settings/tenant-connection#tenant-init")}>
              Settings → Tenant connection → Tenant init
            </a>{" "}
            (or add a credential under{" "}
            <a className="underline" href={to("/admin/settings/tenant-connection#entra-application")}>
              Settings → Tenant connection → Entra application
            </a>
            ), then start a scan on{" "}
            <a className="underline" href={to("/scans")}>
              Scans
            </a>
            .
          </p>
        </div>
      </div>
      {onRetry !== undefined ? (
        <div className="pl-7">
          <Button variant="outline" size="sm" onClick={onRetry} data-testid="error-panel-retry">
            <RefreshCw aria-hidden="true" />
            Check again
          </Button>
        </div>
      ) : null}
    </div>
  );
}
